import type { RouterNavigateOptions } from '@remix-run/router';
import type { To } from 'react-router-dom';

import { matchRoutes } from 'react-router-dom';
import { DEFAULT_PATH_AFTER_LOGIN } from '@/const';
import { useUserStore } from '@/stores/user';
import {
  buildLoginRedirectPath,
  canBypassLoginForAnonymousPath,
  getLoginRedirect,
  hasRequireLoginRoute,
} from './helper';

/**
 * 路由跳转
 */
export const navigate = async function (to: To | null, opts?: RouterNavigateOptions) {
  if (!to) {
    return;
  }

  if (to === '/404') {
    location.href = '/404';
    return;
  }

  if (to === '/') {
    location.href = '/';
    return;
  }

  let targetURL = '';
  if (typeof to === 'string') {
    targetURL = to;
  } else {
    targetURL = `${to.pathname || location.pathname}${to.search}${to.hash}`;
  }
  const { navigate } = window.CustomRouter;
  const { isLogin } = useUserStore.getState();
  const matches = /\?(.*)/.exec(targetURL);
  const searchParams = new URLSearchParams(matches?.[1] || '');
  const redirect = getLoginRedirect(searchParams);
  const routes = matchRoutes(window.customRoutes, targetURL);

  if (hasRequireLoginRoute(routes) && !isLogin) {
    // 保留原始 URL 的完整路径（包括查询参数和 hash）
    const redirectPath = redirect || targetURL;
    return navigate(buildLoginRedirectPath(redirectPath));
  }

  if (to === '/login' && isLogin) {
    return navigate(redirect || DEFAULT_PATH_AFTER_LOGIN, { replace: true });
  }
  return navigate(to, opts);
};

export function handlePermissionError() {
  if (useUserStore.getState().isLogin) {
    return;
  }
  const { hash, pathname, search } = window.location;
  if (['/login', '/maintenance'].includes(pathname)) {
    return;
  }
  if (canBypassLoginForAnonymousPath(pathname)) {
    return;
  }
  navigate(buildLoginRedirectPath(pathname, search, hash));
}

/**
 * 从 URL 中获取项目 ID
 * 支持路径格式：/workspace/projects/:projectId
 * 兼容旧路径格式：/projects/:projectId
 */
export function getProjectIdFromUrl(): string | undefined {
  const pathname = window.location.pathname;
  const match = pathname.match(/\/workspace\/projects\/([^/?]+)/) || pathname.match(/\/projects\/([^/?]+)/);
  return match?.[1];
}
